import { ChakraProvider } from '@chakra-ui/react'
import '@fontsource-variable/geist'
import '@fontsource-variable/geist-mono'
import * as Sentry from '@sentry/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { RouterProvider } from '@tanstack/react-router'
import { Buffer } from 'buffer'
import { ThemeProvider } from 'next-themes'
import React from 'react'
import { createRoot } from 'react-dom/client'
import 'regenerator-runtime/runtime'

import * as api from 'api'

import { Loading } from 'components/Loading'
import { PostHogSetup } from 'components/PostHogProvider'
import { Toaster } from 'components/ui/toaster'

import { system, Fonts } from 'constants/theme'

import { ProvideFeatureCallouts } from 'hooks/useFeatureCallouts'
import { ProvideGoogleMapsApi } from 'hooks/useGoogleMapsApi'
import { ProvideAuthContext, useAuthContext } from 'hooks/useUserContext'
import { ProvideUserPrefs } from 'hooks/useUserPrefsContext'
import { useVersionCheck } from 'hooks/useVersionCheck'

import {
  replayAllowedOn,
  sentryBeforeAddRecordingEvent,
} from 'lib/oneTimeTokenPrivacy'
import { queryClient } from 'lib/react-query'
import {
  sentryBeforeSend,
  sentryBeforeSendTransaction,
} from 'lib/sentryBeforeSend'
import { getUIVersion } from 'lib/uiVersion'

import reportWebVitals from './reportWebVitals'
import { router } from './router'
import { handleStaleChunkPreloadError } from './staleChunkReload'

declare global {
  interface Window {
    api: typeof api
    version: string
    app_version: string
    Buffer: typeof Buffer
    Chargify: any
  }
}

handleStaleChunkPreloadError()

// Apply classic UI class before first render so CSS overrides take effect
// without a flash. This must run synchronously before root.render().
if (getUIVersion() === 'classic') {
  document.documentElement.classList.add('bc-classic')
}

window.api = api
window.version = `v${import.meta.env.VITE_VERSION}`
window.app_version = `v${import.meta.env.VITE_APP_VERSION}`
window.Buffer = Buffer

if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_ENABLED === 'true') {
  // Guard init: a Sentry SDK / integration failure (misconfig, blocked
  // transport, integration setup error) must not throw at module load and
  // white-screen the whole app before React mounts. On failure we simply run
  // this session without error tracking.
  try {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.VITE_SENTRY_ENV || 'unknown',
      integrations: [
        Sentry.tanstackRouterBrowserTracingIntegration(router),
        // Never record a replay on a one-time-token page (/reset, /magic):
        // replay meta events carry the page URL and bypass every hook.
        ...(replayAllowedOn(window.location.pathname)
          ? [
              Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
                beforeAddRecordingEvent: sentryBeforeAddRecordingEvent,
              }),
            ]
          : []),

        // Sentry.feedbackIntegration({
        //   // https://docs.sentry.io/platforms/javascript/user-feedback/configuration/
        //   // Additional SDK configuration goes in here, for example:
        //   showBranding     : false,
        //   buttonLabel      : 'Feedback',
        //   submitButtonLabel: 'Send Feedback',
        //   formTitle        : 'Help us improve your experience on this page.',
        //   autoInject       : false,
        // }),
      ],
      tracePropagationTargets: [
        'localhost',
        /^http:\/\/local-api\.beadedcloud\.com:3030/,
        /^https:\/\/api\.prod\.beadedcloud\.com/,
        /^https:\/\/api\.dev\.beadedcloud\.com/,
        /^https:\/\/api\.beadedcloud\.com/,
      ],

      beforeSend: sentryBeforeSend,
      beforeSendTransaction: sentryBeforeSendTransaction,

      // Matches the release name @sentry/vite-plugin uses (`web@v${appVersion}`
      // in vite.config.ts), since both reference the same `appVersion` source.
      // The plugin's `release.inject` is the canonical path in CI; this fallback
      // covers builds where the plugin is skipped (no SENTRY_AUTH_TOKEN).
      release: `web@v${import.meta.env.VITE_VERSION}`,

      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: 0.1,

      // This sets the sample rate to be 10%. You may want this to be 100% while
      // in development and sample at a lower rate in production
      replaysSessionSampleRate: 0.1,
      // If the entire session is not sampled, use the below sample rate to sample
      // sessions when an error occurs.
      replaysOnErrorSampleRate: 1.0,
    })
  } catch (err) {
    console.error('Sentry init failed; continuing without it:', err)
  }
}

function RouterProviderWrapper() {
  const auth = useAuthContext()
  useVersionCheck()

  if (!auth.authLoaded) return <Loading />

  return <RouterProvider router={router} context={{ auth, queryClient }} />
}

const root = createRoot(document.getElementById('root')!)
root.render(
  <React.StrictMode>
    <ThemeProvider attribute='class' disableTransitionOnChange>
      <ChakraProvider value={system}>
        <Fonts />
        <Toaster />
        <QueryClientProvider client={queryClient}>
          <ProvideAuthContext>
            <ProvideUserPrefs>
              <PostHogSetup>
                <ProvideFeatureCallouts>
                  <ProvideGoogleMapsApi>
                    <RouterProviderWrapper />
                  </ProvideGoogleMapsApi>
                </ProvideFeatureCallouts>
              </PostHogSetup>
            </ProvideUserPrefs>
          </ProvideAuthContext>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </ChakraProvider>
    </ThemeProvider>
  </React.StrictMode>,
)

reportWebVitals()
